import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import Http from "@/lib/Http"
import { ArrangementOrder } from "@/lib/ArrangementOrder"

export default class extends Controller {
  static targets = ["item", "list", "sentinel", "status", "undo", "save", "startFrom"]
  static values = {
    orderUrl: String,
    rowsUrl: String,
    saveUrl: String,
    returnUrl: String,
    windowSize: { type: Number, default: 20 },
    batchSize: { type: Number, default: 100 },
  }

  connect() {
    this.order = new ArrangementOrder()
    this.rows = new Map()
    this.rendered = 0
    this.ready = false
    this.saving = false
    this.extending = false
    this.chain = Promise.resolve()

    this.absorbServerRenderedRows()
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) this.extend()
    })
    this.observer.observe(this.sentinelTarget)

    this.guardUnload = (event) => {
      if (this.order.dirty) event.preventDefault()
    }
    window.addEventListener("beforeunload", this.guardUnload)

    this.loadOrder()
  }

  disconnect() {
    this.observer?.disconnect()
    window.removeEventListener("beforeunload", this.guardUnload)
  }

  up(event) {
    this.reorder(() => this.order.moveBy(this.rowIdFor(event), -1))
  }

  down(event) {
    this.reorder(() => this.order.moveBy(this.rowIdFor(event), 1))
  }

  toTop(event) {
    this.reorder(() => this.order.toTop(this.rowIdFor(event)))
  }

  toBottom(event) {
    this.reorder(() => this.order.toBottom(this.rowIdFor(event)))
  }

  toPosition(event) {
    const input = event.target.closest("[data-reorder-id]")?.querySelector("[data-reorder-input]")
    const position = Number(input?.value)
    if (!Number.isInteger(position) || position < 1) return

    input.value = ""
    this.reorder(() => this.order.moveTo(this.rowIdFor(event), position))
  }

  async startFrom() {
    const key = this.startFromTarget.value
    if (!key || !this.ready) return

    try {
      const { data } = await Http.get(this.orderUrlValue, { params: { start_from: key } })
      this.order.replaceAll(data.ids)
      this.rendered = Math.min(this.windowSizeValue, this.order.length)
      await this.settle()
      window.scrollTo({ top: 0 })
    } catch {
      this.status("error")
    } finally {
      this.startFromTarget.value = ""
    }
  }

  undo() {
    if (this.order.undo()) this.settle()
  }

  async save() {
    if (this.saving || !this.order.dirty) return

    this.saving = true
    this.status("saving")
    this.refreshControls()

    try {
      await Http.patch(this.saveUrlValue, { ids: this.order.ids })
      this.order.clearHistory()
      this.status("saved")

      if (this.hasReturnUrlValue) Turbo.visit(this.returnUrlValue)
    } catch {
      this.status("error")
    } finally {
      this.saving = false
      this.refreshControls()
    }
  }

  leave(event) {
    if (!this.order.dirty) return

    if (window.confirm("You have unsaved changes to your display order. Leave without saving?")) {
      this.order.clearHistory()
    } else {
      event.preventDefault()
    }
  }

  // Run a pure order mutation and reflect it in the DOM when it changes anything.
  reorder(mutate) {
    if (this.ready && mutate()) this.settle()
  }

  settle() {
    this.status(this.order.dirty ? "dirty" : "clean")
    this.refreshControls()
    return this.sync()
  }

  sync() {
    this.chain = this.chain.then(() => this.reconcile())
    return this.chain
  }

  async reconcile() {
    const visible = this.order.ids.slice(0, this.rendered)
    await this.fetchMissing(visible.filter((id) => !this.rows.has(id)))

    let cursor = this.listTarget.firstElementChild
    visible.forEach((id) => {
      const row = this.rows.get(id)
      if (!row) return

      if (row === cursor) {
        cursor = cursor.nextElementSibling
      } else {
        this.listTarget.insertBefore(row, cursor)
      }
    })

    while (cursor) {
      const next = cursor.nextElementSibling
      cursor.remove()
      cursor = next
    }

    this.renumber()
  }

  async fetchMissing(missing) {
    for (let start = 0; start < missing.length; start += this.batchSizeValue) {
      const batch = missing.slice(start, start + this.batchSizeValue)
      const { data } = await Http.get(this.rowsUrlValue, {
        params: { ids: batch, from: this.order.indexOf(batch[0]) + 1 },
        headers: { Accept: "text/html" },
      })
      this.absorb(data)
    }
  }

  absorb(html) {
    const template = document.createElement("template")
    template.innerHTML = html.trim()
    template.content.querySelectorAll("[data-reorder-id]").forEach((row) => {
      this.rows.set(Number(row.dataset.reorderId), row)
    })
  }

  absorbServerRenderedRows() {
    this.itemTargets.forEach((row) => this.rows.set(Number(row.dataset.reorderId), row))
    this.rendered = this.itemTargets.length
  }

  async loadOrder() {
    try {
      const { data } = await Http.get(this.orderUrlValue)
      this.order.reset(data.ids)
      this.ready = true
      this.element.dataset.reorderReady = "true"
      this.refreshControls()
      await this.extend()
    } catch {
      this.status("error")
    }
  }

  async extend() {
    if (!this.ready || this.extending || this.rendered >= this.order.length) return

    this.extending = true
    this.rendered = Math.min(this.rendered + this.windowSizeValue, this.order.length)
    await this.sync()
    this.extending = false

    if (this.sentinelVisible) await this.extend()
  }

  renumber() {
    Array.from(this.listTarget.children).forEach((row, index) => {
      const label = row.querySelector("[data-reorder-position]")
      const value = String(index + 1)
      if (label && label.textContent !== value) label.textContent = value
    })
  }

  refreshControls() {
    const blocked = !this.order.dirty || this.saving
    if (this.hasUndoTarget) this.undoTarget.disabled = blocked
    if (this.hasSaveTarget) this.saveTarget.disabled = blocked
  }

  status(state) {
    if (!this.hasStatusTarget) return

    this.statusTarget.querySelectorAll("[data-reorder-status]").forEach((label) => {
      label.classList.toggle("hidden", label.dataset.reorderStatus !== state)
    })
  }

  rowIdFor(event) {
    return Number(event.target.closest("[data-reorder-id]")?.dataset.reorderId)
  }

  get sentinelVisible() {
    return this.sentinelTarget.getBoundingClientRect().top < window.innerHeight
  }
}
